import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, DatePicker, TimePicker } from 'antd';
import dayjs from 'dayjs';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  InfoCircleIcon,
  PersonIcon,
  DescriptionLinesIcon,
  LocationPinIcon,
  CrTransferIcon,
  CrVipIcon,
  CrCallbackIcon,
  AttachmentIcon,
  AssignmentIcon,
  CameraIcon,
  TemplateDocIcon,
  CaretIcon,
  CheckmarkIcon,
  CloseIcon,
  FileXlsxIcon,
} from '../../assets/icons';

/* ------------------------------------------------------------------ *
 * Small shared bits
 * ------------------------------------------------------------------ */
type IconType = typeof InfoCircleIcon;

function Card({
  Icon,
  title,
  divider = true,
  iconWrap = 'bg-brand-shade text-brand',
  children,
}: {
  Icon: IconType;
  title: string;
  divider?: boolean;
  /** Icon-circle color classes (bg + text) — defaults to the brand blue. */
  iconWrap?: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-5 rounded-xl border border-[#eef2f7] bg-white p-6 shadow-card">
      <div
        className={[
          'flex items-center gap-2.5',
          divider ? '-mx-6 border-b border-[#e2e8f0] px-6 pb-4' : '',
        ].join(' ')}
      >
        <span
          className={['flex size-8 shrink-0 items-center justify-center rounded-full', iconWrap].join(
            ' ',
          )}
        >
          <Icon className="size-5" />
        </span>
        <h2 className="text-base font-semibold text-ink">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-xs font-medium text-[#6b7280]">{label}</span>
      {children}
    </label>
  );
}

const INPUT =
  'w-full rounded-lg border border-[#e5e7eb] bg-[#f8fafc] px-3.5 py-2.5 text-sm text-ink placeholder:text-[#9ca3af] transition-colors focus:border-brand focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand/15';

/** Uppercase, tracked field label — used by the Call-back Details section (per the design). */
const UPPER_LABEL = 'text-xs font-semibold uppercase tracking-[0.7px] text-[#334155]';

/* ------------------------------------------------------------------ *
 * Priority
 * ------------------------------------------------------------------ */
const PRIORITIES: { key: string; label: string; dot: string }[] = [
  { key: 'low', label: 'Low', dot: 'bg-[#94a3b8]' },
  { key: 'medium', label: 'Medium', dot: 'bg-[#f59e0b]' },
  { key: 'high', label: 'High', dot: 'bg-[#f97316]' },
  { key: 'critical', label: 'Critical', dot: 'bg-[#ef4444]' },
];

/* ------------------------------------------------------------------ *
 * Location tree (static shape from the design)
 * ------------------------------------------------------------------ */
interface LocNode {
  id: string;
  label: string;
  children?: LocNode[];
}
const LOCATION_TREE: LocNode[] = [
  {
    id: 'alwar',
    label: 'Alwar AC',
    children: [
      { id: 'urban', label: 'Urban' },
      {
        id: 'rural',
        label: 'Rural',
        children: [
          {
            id: 'ps',
            label: 'Panchayat Samiti',
            children: [
              {
                id: 'gp',
                label: 'Gram Panchayat',
                children: [
                  { id: 'village', label: 'Village', children: [{ id: 'booth', label: 'Booth' }] },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

function LocationTree({ selected, onSelect }: { selected: string; onSelect: (id: string) => void }) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setCollapsed((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const render = (node: LocNode, depth: number) => {
    const kids = node.children ?? [];
    const hasKids = kids.length > 0;
    const open = !collapsed.has(node.id);
    const isSel = selected === node.id;
    const isRoot = depth === 0;
    return (
      <div key={node.id}>
        <div
          className={[
            'flex items-center gap-2 rounded-lg py-1.5',
            isSel ? 'bg-[#eff6ff] px-2' : 'px-0',
          ].join(' ')}
        >
          {hasKids ? (
            <button
              type="button"
              onClick={() => toggle(node.id)}
              aria-label={open ? 'Collapse' : 'Expand'}
              className="flex size-4 shrink-0 items-center justify-center text-[#94a3b8]"
            >
              <CaretIcon className={['size-4 transition-transform', open ? '' : '-rotate-90'].join(' ')} />
            </button>
          ) : (
            <span className="size-4 shrink-0" />
          )}
          {!isRoot && (
            <button
              type="button"
              role="checkbox"
              aria-checked={isSel}
              onClick={() => onSelect(node.id)}
              className={[
                'flex size-[18px] shrink-0 items-center justify-center rounded transition-colors',
                isSel ? 'bg-brand text-white' : 'border border-[#cbd5e1] bg-white',
              ].join(' ')}
            >
              {isSel && <CheckmarkIcon className="size-3.5" />}
            </button>
          )}
          <button
            type="button"
            onClick={() => (hasKids ? toggle(node.id) : onSelect(node.id))}
            className={[
              'flex-1 truncate text-left text-sm',
              isRoot ? 'font-semibold text-ink' : isSel ? 'font-medium text-brand' : 'text-ink',
            ].join(' ')}
          >
            {node.label}
          </button>
          {isSel && (
            <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.5px] text-brand">
              Selected
            </span>
          )}
        </div>
        {hasKids && open && (
          <div className="ml-[19px] flex flex-col gap-0.5 border-l border-[#e2e8f0] pl-2">
            {kids.map((c) => render(c, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return <div className="flex flex-col gap-0.5">{LOCATION_TREE.map((n) => render(n, 0))}</div>;
}

/* ------------------------------------------------------------------ *
 * Attachments
 * ------------------------------------------------------------------ */
interface Attachment {
  name: string;
  kind: 'image' | 'doc';
}

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */
export default function CreateCitizenRequestPage() {
  const navigate = useNavigate();

  const [crType, setCrType] = useState('Public Grievance');
  const [category, setCategory] = useState('Water Supply');
  const [source, setSource] = useState('Phone Call');
  const [dateReceived, setDateReceived] = useState<dayjs.Dayjs | null>(null);
  const [priority, setPriority] = useState('medium');

  const [fullName, setFullName] = useState('');
  const [mobile, setMobile] = useState('');
  const [altNumber, setAltNumber] = useState('');
  const [gender, setGender] = useState('');
  const [address, setAddress] = useState('');

  // Sifarish / Transfer details — shown only when the CR type is a transfer request.
  const [currentLocation, setCurrentLocation] = useState('');
  const [requestedLocation, setRequestedLocation] = useState('');
  const [referenceDetails, setReferenceDetails] = useState('');

  // VIP Grievance details — shown only when the CR type is a VIP grievance.
  const [vipName, setVipName] = useState('');
  const [vipReference, setVipReference] = useState('');
  const [preferredCallback, setPreferredCallback] = useState('');
  const [followUpDate, setFollowUpDate] = useState<dayjs.Dayjs | null>(null);

  // Call-back Request details — shown only when the CR type is a call-back request.
  const [callbackRequired, setCallbackRequired] = useState('Yes');
  const [preferredDate, setPreferredDate] = useState<dayjs.Dayjs | null>(null);
  const [preferredTime, setPreferredTime] = useState<dayjs.Dayjs | null>(null);
  const [callbackNotes, setCallbackNotes] = useState('');

  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');

  const [location, setLocation] = useState('rural');
  const [attachments, setAttachments] = useState<Attachment[]>([
    { name: 'water_issue.jpg', kind: 'image' },
    { name: 'complaint_letter.pdf', kind: 'doc' },
  ]);

  const [department, setDepartment] = useState('Water Supply');
  const [assignTo, setAssignTo] = useState('Ravi Kumar');
  const [requestedDate, setRequestedDate] = useState<dayjs.Dayjs | null>(dayjs('2026-07-31'));

  const addFiles = (files: FileList | null, kind: Attachment['kind']) => {
    if (!files?.length) return;
    setAttachments((prev) => [...prev, ...Array.from(files).map((f) => ({ name: f.name, kind }))]);
  };
  const removeFile = (idx: number) =>
    setAttachments((prev) => prev.filter((_, i) => i !== idx));

  const goBack = () => navigate('/user/citizen-requests');

  // Type-specific sections. Sifarish and VIP each replace the generic Location Details
  // card with their own details section; VIP also folds Subject/Description into it.
  const isSifarish = crType === 'Sifarish / Transfers';
  const isVip = crType === 'VIP Grievance';
  const isCallback = crType === 'Call-back Request';
  // Sifarish and VIP swap the sidebar's Location Details for their own left-column section;
  // Call-back keeps Location Details but drops the Attachments card.
  const hasTypeSection = isSifarish || isVip;

  return (
    <div className="flex flex-col gap-6 pb-24">
      {/* Header — single box */}
      <div className="flex flex-col gap-3 rounded-xl border border-[#eef2f7] bg-white p-6 shadow-card lg:flex-row lg:items-start lg:justify-between">
        <div className="flex flex-col gap-1">
          <Breadcrumbs
            items={[
              { label: 'Citizen Requests', to: '/user/citizen-requests' },
              { label: 'New Request' },
            ]}
          />
          <h1 className="text-2xl font-semibold text-ink">Create Citizen Request</h1>
          <p className="text-sm text-ink-secondary">
            Log and assign new public grievances or service requests to the appropriate departments.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            onClick={goBack}
            className="rounded-full border border-[#e5e7eb] bg-white px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-brand-shade"
          >
            Discard Changes
          </button>
          <button
            type="button"
            className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark"
          >
            Save &amp; Assign
            <CaretIcon className="size-4" />
          </button>
        </div>
      </div>

      {/* Body grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left column */}
        <div className="flex flex-col gap-6 lg:col-span-2">
          {/* Request Information */}
          <Card Icon={InfoCircleIcon} title="Request Information">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label="CR Type">
                <Select
                  className="w-full"
                  value={crType}
                  onChange={setCrType}
                  options={['Public Grievance', 'Sifarish / Transfers', 'VIP Grievance', 'Call-back Request', 'Invitation', 'Service Request'].map((o) => ({ value: o, label: o }))}
                />
              </Field>
              <Field label="Category">
                <Select
                  className="w-full"
                  value={category}
                  onChange={setCategory}
                  options={['Water Supply', 'Electricity', 'Roads', 'Health', 'Education'].map((o) => ({ value: o, label: o }))}
                />
              </Field>
              <Field label="Source">
                <Select
                  className="w-full"
                  value={source}
                  onChange={setSource}
                  options={['Phone Call', 'Walk-in', 'Web', 'Field Visit'].map((o) => ({ value: o, label: o }))}
                />
              </Field>
              <Field label="Date Received">
                <DatePicker
                  className="w-full"
                  showTime
                  format="DD-MMM-YYYY, hh:mm A"
                  placeholder="Select date & time"
                  value={dateReceived}
                  onChange={setDateReceived}
                />
              </Field>
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-[#6b7280]">Priority</span>
              <div className="flex flex-wrap gap-3">
                {PRIORITIES.map((p) => {
                  const active = priority === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setPriority(p.key)}
                      className={[
                        'flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors',
                        active
                          ? 'border-brand text-brand'
                          : 'border-[#e5e7eb] text-ink-secondary hover:border-brand/40',
                      ].join(' ')}
                    >
                      <span className={['size-2 rounded-full', p.dot].join(' ')} />
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </Card>

          {/* Citizen / Caller Information — call-backs collect only the caller's contact */}
          <Card Icon={PersonIcon} title={isCallback ? 'Caller Information' : 'Citizen Information'}>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              <Field label="Full Name">
                <input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter citizen name"
                  className={INPUT}
                />
              </Field>
              <Field label="Mobile Number">
                <input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+91" className={INPUT} />
              </Field>
              <Field label="Alternate Number">
                <input value={altNumber} onChange={(e) => setAltNumber(e.target.value)} placeholder="+91" className={INPUT} />
              </Field>
            </div>
            {!isCallback && (
              <>
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-medium text-[#6b7280]">Gender</span>
                  <div className="flex items-center gap-6">
                    {['Male', 'Female', 'Other'].map((g) => (
                      <label key={g} className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                        <input
                          type="radio"
                          name="gender"
                          checked={gender === g}
                          onChange={() => setGender(g)}
                          className="size-4 accent-brand"
                        />
                        {g}
                      </label>
                    ))}
                  </div>
                </div>
                <Field label="Full Address">
                  <textarea
                    rows={3}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Street, landmark, door number..."
                    className={[INPUT, 'resize-none'].join(' ')}
                  />
                </Field>
              </>
            )}
          </Card>

          {/* Sifarish / Transfer Details — only for transfer requests */}
          {isSifarish && (
            <Card Icon={CrTransferIcon} title="Sifarish / Transfer Details">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Current Location / Department">
                  <input
                    value={currentLocation}
                    onChange={(e) => setCurrentLocation(e.target.value)}
                    placeholder="e.g. City Police Station"
                    className={INPUT}
                  />
                </Field>
                <Field label="Requested Location / Department">
                  <input
                    value={requestedLocation}
                    onChange={(e) => setRequestedLocation(e.target.value)}
                    placeholder="e.g. Traffic Branch HQ"
                    className={INPUT}
                  />
                </Field>
              </div>
              <Field label="Reference Details (Who recommended?)">
                <input
                  value={referenceDetails}
                  onChange={(e) => setReferenceDetails(e.target.value)}
                  placeholder="Name of MLA, Sarpanch, etc."
                  className={INPUT}
                />
              </Field>
            </Card>
          )}

          {/* VIP Request Details — only for VIP grievances (folds in Subject + Description) */}
          {isVip && (
            <Card
              Icon={CrVipIcon}
              title="VIP Request Details"
              iconWrap="bg-[#fef3c7] text-[#b45309]"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="VIP Name">
                  <input
                    value={vipName}
                    onChange={(e) => setVipName(e.target.value)}
                    placeholder="Enter VIP name..."
                    className={INPUT}
                  />
                </Field>
                <Field label="Reference / Contact">
                  <input
                    value={vipReference}
                    onChange={(e) => setVipReference(e.target.value)}
                    placeholder="Designation or Reference..."
                    className={INPUT}
                  />
                </Field>
              </div>
              <Field label="Request Subject">
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Short summary of the issue"
                  className={INPUT}
                />
              </Field>
              <Field label="Description">
                <textarea
                  rows={5}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Provide detailed information about the grievance..."
                  className={[INPUT, 'resize-none'].join(' ')}
                />
              </Field>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Preferred Callback">
                  <input
                    value={preferredCallback}
                    onChange={(e) => setPreferredCallback(e.target.value)}
                    placeholder="Time or Number"
                    className={INPUT}
                  />
                </Field>
                <Field label="Follow-up Date">
                  <DatePicker
                    className="w-full"
                    format="DD-MMM-YYYY"
                    placeholder="Select date"
                    value={followUpDate}
                    onChange={setFollowUpDate}
                  />
                </Field>
              </div>
            </Card>
          )}

          {/* Call-back Details — only for call-back requests */}
          {isCallback && (
            <Card Icon={CrCallbackIcon} title="Call-back Details">
              <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
                <span className={UPPER_LABEL}>Callback Required?</span>
                <div className="flex items-center gap-6">
                  {['Yes', 'No'].map((o) => (
                    <label key={o} className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                      <input
                        type="radio"
                        name="callbackRequired"
                        checked={callbackRequired === o}
                        onChange={() => setCallbackRequired(o)}
                        className="size-4 accent-brand"
                      />
                      {o}
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <label className="flex flex-col gap-2">
                  <span className={UPPER_LABEL}>Preferred Date</span>
                  <DatePicker
                    className="w-full"
                    format="MM/DD/YYYY"
                    placeholder="mm/dd/yyyy"
                    value={preferredDate}
                    onChange={setPreferredDate}
                  />
                </label>
                <label className="flex flex-col gap-2">
                  <span className={UPPER_LABEL}>Preferred Time</span>
                  <TimePicker
                    className="w-full"
                    use12Hours
                    format="hh:mm A"
                    placeholder="--:-- --"
                    value={preferredTime}
                    onChange={setPreferredTime}
                  />
                </label>
              </div>
              <label className="flex flex-col gap-2">
                <span className={UPPER_LABEL}>Notes / Reason for Callback</span>
                <textarea
                  rows={4}
                  value={callbackNotes}
                  onChange={(e) => setCallbackNotes(e.target.value)}
                  placeholder="Detailed notes about the callback..."
                  className={[INPUT, 'resize-none'].join(' ')}
                />
              </label>
            </Card>
          )}

          {/* Request Description — VIP and Call-back fold this into their own sections */}
          {!isVip && !isCallback && (
            <Card Icon={DescriptionLinesIcon} title="Request Description">
              <Field label="Subject">
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Short summary of the issue"
                  className={INPUT}
                />
              </Field>
              <Field label="Description">
                <textarea
                  rows={5}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Provide detailed information about the grievance..."
                  className={[INPUT, 'resize-none'].join(' ')}
                />
              </Field>
            </Card>
          )}
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-6">
          {/* Location Details — replaced by the type-specific details section for Sifarish/VIP */}
          {!hasTypeSection && (
            <Card Icon={LocationPinIcon} title="Location Details">
              <LocationTree selected={location} onSelect={setLocation} />
            </Card>
          )}

          {/* Attachments — not collected for call-back requests */}
          {!isCallback && (
          <Card Icon={AttachmentIcon} title="Attachments">
            <div className="grid grid-cols-2 gap-4">
              <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[#d1d5db] bg-[#f8fafc] py-6 text-center transition-colors hover:border-brand/50">
                <CameraIcon className="size-6 text-ink-secondary" />
                <span className="text-xs font-semibold uppercase tracking-[0.3px] text-ink-secondary">Photo</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => addFiles(e.target.files, 'image')} />
              </label>
              <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[#d1d5db] bg-[#f8fafc] py-6 text-center transition-colors hover:border-brand/50">
                <TemplateDocIcon className="h-6 w-[18px] text-ink-secondary" />
                <span className="text-xs font-semibold uppercase tracking-[0.3px] text-ink-secondary">Document</span>
                <input type="file" accept=".pdf,.doc,.docx" multiple className="hidden" onChange={(e) => addFiles(e.target.files, 'doc')} />
              </label>
            </div>
            {attachments.length > 0 && (
              <div className="flex flex-col gap-2">
                {attachments.map((a, i) => (
                  <div key={`${a.name}-${i}`} className="flex items-center gap-2 rounded-lg bg-[#f8fafc] px-3 py-2">
                    {a.kind === 'image' ? (
                      <CameraIcon className="size-4 shrink-0 text-brand" />
                    ) : (
                      <FileXlsxIcon className="size-4 shrink-0 text-[#ef4444]" />
                    )}
                    <span className="flex-1 truncate text-xs text-ink">{a.name}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${a.name}`}
                      onClick={() => removeFile(i)}
                      className="shrink-0 text-ink-secondary transition-colors hover:text-red-500"
                    >
                      <CloseIcon className="size-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>
          )}

          {/* Assignment */}
          <Card Icon={AssignmentIcon} title="Assignment">
            <Field label="Department">
              <Select
                className="w-full"
                value={department}
                onChange={setDepartment}
                options={['Water Supply', 'Electricity', 'Public Works', 'Revenue'].map((o) => ({ value: o, label: o }))}
              />
            </Field>
            <Field label="Assign To">
              <Select
                className="w-full"
                value={assignTo}
                onChange={setAssignTo}
                options={['Ravi Kumar', 'Ankit', 'Kavi', 'Ram'].map((o) => ({ value: o, label: o }))}
              />
            </Field>
            {!isCallback && (
              <Field label="Requested Date">
                <DatePicker
                  className="w-full"
                  format="MM/DD/YYYY"
                  value={requestedDate}
                  onChange={setRequestedDate}
                />
              </Field>
            )}
          </Card>
        </div>
      </div>

      {/* Sticky footer */}
      <div className="fixed inset-x-0 bottom-0 z-10 border-t border-[#dbeafe] bg-[#eff6ff] px-6 py-3 lg:left-[280px]">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              <span className="flex size-8 items-center justify-center rounded-full border-2 border-white bg-brand text-[11px] font-semibold text-white">
                RK
              </span>
              <span className="flex size-8 items-center justify-center rounded-full border-2 border-white bg-[#dbeafe] text-[11px] font-semibold text-brand">
                +2
              </span>
            </div>
            <span className="text-sm text-ink">Assigned to Ravi Kumar and 2 others.</span>
          </div>
          <div className="flex items-center gap-5">
            <button type="button" className="text-sm font-semibold text-ink transition-colors hover:text-brand">
              Save Draft
            </button>
            <button
              type="button"
              onClick={goBack}
              className="rounded-full bg-brand px-6 py-2.5 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark"
            >
              Create &amp; Finish
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
